import React from 'react'
import { GoogleMap as GoogleMapAPI, LoadScript } from '@react-google-maps/api'

import MapMarker from './MapMarker'
import { MapLocationProps, MarkerProps, StoreMapProps } from './MapTypes'
import { MapContainer } from './styles'

const containerStyle = {
  width: '100%',
  height: '100%',
}

const ZOOM = {
  default: 4,
  selected: 8,
}

export const StoreMap = React.memo(
  ({
    apiKey,
    markers = [],
    center,
    selectedMarkerId,
    defaultPinColor = null,
    hoverPinColor = null,
    selectedPinColor = null,
    defaultZoom,
    selectedPinZoom,
    showMarkerInfoWindow = true,
    mapReadyCallback,
    pinClickCallback,
    mapControlOptions,
    storeDetailsHref,
    cardText,
    scriptLoaded,
  }: StoreMapProps) => {
    // Fit bounds =>> View all the pins at one single frame
    const fitBounds = () => {
      if (map) {
        const bounds = new window.google.maps.LatLngBounds()
        markers.map((marker: MarkerProps) => {
          bounds.extend({ lat: marker.position.lat, lng: marker.position.lng })
        })
        map.fitBounds(bounds)
      }
    }

    // Map Instace
    const [map, setMap] = React.useState<google.maps.Map | null>()

    // Initializing map instance
    const onLoad = React.useCallback((map: google.maps.Map) => {
      setMap(map)
      map.setZoom(defaultZoom || ZOOM.default)
      fitBounds()

      if (typeof mapReadyCallback === 'function') {
        mapReadyCallback(true)
      }
    }, [])

    const onUnmount = React.useCallback((_map: google.maps.Map) => {
      setMap(null)
    }, [])

    // Marker Click handler
    const handleMarkerClick = (
      markerPosition: MapLocationProps,
      isInfoWindowOpen: boolean,
      markerId: number | null,
    ) => {
      if (map) {
        if (isInfoWindowOpen) {
          map.setZoom(selectedPinZoom || ZOOM.selected)
          map.panTo(markerPosition)
          if (typeof pinClickCallback === 'function') {
            pinClickCallback(markerId || 0)
          }
        } else {
          map.setZoom(defaultZoom || ZOOM.default)
          if (markers.length > 1) {
            fitBounds()
          }
        }
      }
    }

    return (
      <MapContainer>
        {!scriptLoaded ? (
          <LoadScript
            id="google-map"
            googleMapsApiKey={apiKey || ''}
            libraries={['places']}
            loadingElement={
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={`https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyC3guylA1wJyOGOdObrlwSoTC8752FKKBQ&zoom=14&size=640x190&scale=2&markers=color:red%7C${center.lat},${center.lng}&style=feature:poi%7Cvisibility:off`}
                alt="Placeholder Map"
              />
            }
          >
            <GoogleMapAPI
              mapContainerStyle={containerStyle}
              center={center}
              zoom={6}
              onLoad={onLoad}
              onUnmount={onUnmount}
              {...(mapControlOptions && { options: { ...mapControlOptions } })}
            >
              {markers.map((marker, index) => (
                <MapMarker
                  key={`${marker.markerId}-${index}`}
                  onMarkerClick={handleMarkerClick}
                  {...marker}
                  counter={index + 1}
                  mapInstance={map}
                  selectedMarkerId={selectedMarkerId}
                  defaultPinColor={defaultPinColor}
                  hoverPinColor={hoverPinColor}
                  selectedPinColor={selectedPinColor}
                  showMarkerInfoWindow={showMarkerInfoWindow}
                  storeDetailsHref={storeDetailsHref}
                  cardText={cardText}
                />
              ))}
            </GoogleMapAPI>
          </LoadScript>
        ) : (
          <GoogleMapAPI
            mapContainerStyle={containerStyle}
            center={center}
            zoom={6}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {markers.map((marker, index) => (
              <MapMarker
                key={`${marker.markerId}-${index}`}
                onMarkerClick={handleMarkerClick}
                {...marker}
                counter={index + 1}
                mapInstance={map}
                selectedMarkerId={selectedMarkerId}
                defaultPinColor={defaultPinColor}
                hoverPinColor={hoverPinColor}
                selectedPinColor={selectedPinColor}
                showMarkerInfoWindow={showMarkerInfoWindow}
                storeDetailsHref={storeDetailsHref}
                cardText={cardText}
              />
            ))}
          </GoogleMapAPI>
        )}
      </MapContainer>
    )
  },
)

export default StoreMap
